import { type ClassValue, clsx } from 'clsx'
import { extendTailwindMerge } from 'tailwind-merge'

/**
 * The in-app `--text-*` size tokens declared in `styles/globals.css`.
 *
 * @remarks
 * `utils.test.ts` checks this list and `LANDING_TEXT_TOKENS` against the
 * stylesheet, so a new size token fails the test until it is listed.
 */
export const TEXT_SIZE_TOKENS = [
    'display',
    'h1',
    'h2',
    'body',
    'body-sm',
    'meta',
] as const

/** The landing's `--text-lp-*` size tokens (cleanup spec §3.5). */
export const LANDING_TEXT_TOKENS = [
    'lp-hero',
    'lp-hero-md',
    'lp-hero-lg',
    'lp-hero-sub',
    'lp-hero-sub-md',
    'lp-heading',
    'lp-heading-md',
    'lp-heading-closing',
    'lp-heading-closing-md',
    'lp-numeral',
    'lp-numeral-md',
    'lp-title',
    'lp-title-md',
    'lp-title-sm',
    'lp-meaning',
    'lp-meaning-md',
    'lp-dropcap',
    'lp-question',
    'lp-question-md',
    'lp-wordmark',
    'lp-gloss',
    'lp-gloss-md',
    'lp-operator',
    'lp-marker',
    'lp-lede',
    'lp-copy',
    'lp-aside',
    'lp-specimen-story-sm',
    'lp-colophon',
    'lp-fine',
    'lp-fine-mono',
    'lp-rhythm',
    'lp-cta-sm',
    'lp-cta',
    'lp-cta-lg',
    'lp-link',
    'lp-nav',
    'lp-caption',
    'lp-caption-sm',
    'lp-kicker',
    'lp-kicker-lg',
    'lp-eyebrow-sm',
    'lp-eyebrow-md',
    'lp-eyebrow-lg',
] as const

/** The landing's `--tracking-lp-*` ladder (cleanup spec §3.5). */
export const LANDING_TRACKING_TOKENS = [
    'lp-4',
    'lp-8',
    'lp-16',
    'lp-18',
    'lp-22',
    'lp-28',
    'lp-32',
    'lp-40',
    'lp-50',
] as const

// tailwind-merge only knows Tailwind's default theme. Without this it reads
// text-meta and the other size tokens as colours, and cn() drops them
// whenever a text colour sits beside them. The two @utility classes have no
// theme key, so they join their class groups directly.
const twMerge = extendTailwindMerge({
    extend: {
        theme: {
            text: [...TEXT_SIZE_TOKENS, ...LANDING_TEXT_TOKENS],
            leading: ['prose'],
            tracking: [...LANDING_TRACKING_TOKENS],
        },
        classGroups: { pb: ['pb-safe'], translate: ['rule-baseline'] },
    },
})

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs))
}
